import * as THREE from 'three';
import {AbstractPeriodicIntersectionSolver} from './AbstractPeriodicIntersectionSolver';
import type {CircleIntersectionDefinition2D} from './CircleCircleIntersectionSolver';

/**
 * 二维有限线段定义。
 */
export interface LineSegmentIntersectionDefinition2D {
    /** 线段起点。 */
    start: THREE.Vector2;
    /** 线段终点。 */
    end: THREE.Vector2;
}

/**
 * 圆与有限线段的单个真实交点。
 */
export interface CircleLineSegmentIntersection2D {
    /** 二维交点坐标。 */
    point: THREE.Vector2;
    /** 交点在圆上的角参数，范围 `[0, 2π)`。 */
    circleAngle: number;
    /** 交点在有限线段上的参数，范围 `[0, 1]`。 */
    lineParameter: number;
}

/**
 * 圆与有限线段的二维真实交点求解器。
 *
 * 职责：
 * 1. 用单位直线法向计算圆心到支撑直线的有向距离与垂足；
 * 2. 按垂距与半径的关系区分无交点、相切和双交点；
 * 3. 对双交点从垂足沿直线方向偏移半弦长；
 * 4. 将支撑直线候选点过滤到有限线段参数域。
 *
 * 该类只处理二维数学定义，不依赖草图对象、参考面或显示几何。
 */
export class CircleLineSegmentIntersectionSolver extends AbstractPeriodicIntersectionSolver {
    /** 归一化相交关系判定容差。 */
    private static readonly RELATIVE_TOLERANCE = 1e-12;

    /** 线段参数域边界容差。 */
    private static readonly PARAMETER_TOLERANCE = 1e-10;

    /** 交点隐式方程残差容差。 */
    private static readonly IMPLICIT_RESIDUAL_TOLERANCE = 1e-10;

    /**
     * 求解圆与有限线段的全部真实交点。
     *
     * @param circle 二维圆定义。
     * @param lineSegment 二维有限线段定义。
     * @returns 按线段参数升序排列的真实交点。
     */
    public solve(
        circle: CircleIntersectionDefinition2D,
        lineSegment: LineSegmentIntersectionDefinition2D,
    ): CircleLineSegmentIntersection2D[] {
        if (!this.hasValidDefinition(circle, lineSegment)) {
            console.error('[CircleLineSegmentIntersectionSolver] 圆或线段定义无效，无法计算真实交点。', {
                circle,
                lineSegment,
            });
            return [];
        }

        const lineDirection = lineSegment.end.clone().sub(lineSegment.start);
        const lineLength = lineDirection.length();
        const geometryScale = Math.max(
            circle.radius,
            lineSegment.start.distanceTo(circle.center),
            lineSegment.end.distanceTo(circle.center),
            lineLength,
        );
        const degenerateLineTolerance = Math.max(
            geometryScale * CircleLineSegmentIntersectionSolver.RELATIVE_TOLERANCE,
            Number.EPSILON,
        );
        if (lineLength <= degenerateLineTolerance) {
            console.error('[CircleLineSegmentIntersectionSolver] 线段退化为点，无法计算真实交点。', {
                circle,
                lineSegment,
            });
            return [];
        }

        const unitDirection = lineDirection.clone().multiplyScalar(1 / lineLength);
        const unitNormal = new THREE.Vector2(-unitDirection.y, unitDirection.x);
        const centerFromLineStart = circle.center.clone().sub(lineSegment.start);
        const signedDistance = unitNormal.dot(centerFromLineStart);
        const normalizedDistance = Math.abs(signedDistance) / geometryScale;
        const normalizedRadius = circle.radius / geometryScale;
        const tolerance = CircleLineSegmentIntersectionSolver.RELATIVE_TOLERANCE;

        // 圆心到支撑直线的距离大于半径时无交点。
        if (normalizedDistance - normalizedRadius > tolerance) {
            return [];
        }

        // 垂足由圆心沿单位法向退回有向距离得到。
        const footPoint = circle.center.clone().addScaledVector(unitNormal, -signedDistance);
        const supportLinePoints: THREE.Vector2[] = [];

        if (Math.abs(normalizedDistance - normalizedRadius) <= tolerance) {
            supportLinePoints.push(footPoint);
        }
        else {
            // 半弦长在特征尺度内求解，避免小尺度交点被固定世界容差吞掉。
            const normalizedHalfChord = Math.sqrt(Math.max(
                0,
                normalizedRadius * normalizedRadius - normalizedDistance * normalizedDistance,
            ));
            const halfChord = normalizedHalfChord * geometryScale;
            supportLinePoints.push(
                footPoint.clone().addScaledVector(unitDirection, -halfChord),
                footPoint.clone().addScaledVector(unitDirection, halfChord),
            );
        }

        const residualTolerance = geometryScale
            * geometryScale
            * CircleLineSegmentIntersectionSolver.IMPLICIT_RESIDUAL_TOLERANCE;
        const intersections: CircleLineSegmentIntersection2D[] = [];

        supportLinePoints.forEach((point) => {
            const lineParameter = point.clone().sub(lineSegment.start).dot(unitDirection) / lineLength;
            if (
                lineParameter < -CircleLineSegmentIntersectionSolver.PARAMETER_TOLERANCE
                || lineParameter > 1 + CircleLineSegmentIntersectionSolver.PARAMETER_TOLERANCE
            ) {
                return;
            }

            const normalizedLineParameter = THREE.MathUtils.clamp(lineParameter, 0, 1);
            const normalizedPoint = lineSegment.start.clone().addScaledVector(
                lineDirection,
                normalizedLineParameter,
            );
            const circleResidual = Math.abs(
                normalizedPoint.distanceToSquared(circle.center) - circle.radius * circle.radius,
            );
            if (circleResidual > residualTolerance) {
                return;
            }

            intersections.push({
                point: normalizedPoint,
                circleAngle: this.normalizeAngle(Math.atan2(
                    normalizedPoint.y - circle.center.y,
                    normalizedPoint.x - circle.center.x,
                )),
                lineParameter: normalizedLineParameter,
            });
        });

        return intersections.sort((left, right) => left.lineParameter - right.lineParameter);
    }

    /**
     * 判断圆与线段定义是否可参与解析求交。
     *
     * @param circle 圆定义。
     * @param lineSegment 线段定义。
     * @returns 坐标有限且圆半径为正时返回 true。
     */
    private hasValidDefinition(
        circle: CircleIntersectionDefinition2D,
        lineSegment: LineSegmentIntersectionDefinition2D,
    ): boolean {
        return Number.isFinite(circle.center.x)
            && Number.isFinite(circle.center.y)
            && Number.isFinite(circle.radius)
            && circle.radius > 0
            && Number.isFinite(lineSegment.start.x)
            && Number.isFinite(lineSegment.start.y)
            && Number.isFinite(lineSegment.end.x)
            && Number.isFinite(lineSegment.end.y);
    }

}
